/**
 * Who may see the Exam Integrity page.
 *
 * That page shows other players' integrity verdicts, their device and address
 * context and their timing, so it is restricted to the one reviewer account the
 * owner designates rather than to every admin.
 *
 * This is the convenience half only: it keeps the page out of the sidebar and out
 * of the router for everyone else. The boundary that actually matters is on the
 * backend, where `isIntegrityReviewer` refuses the endpoint with a 403 (see
 * middlewares/adminAuth.js). Anyone can edit their own browser storage, so a
 * client-side check can never be the protection — only the tidiness.
 *
 * The list can be overridden per build with REACT_APP_INTEGRITY_REVIEWER_EMAILS,
 * comma separated, which must then match the backend's INTEGRITY_REVIEWER_EMAILS.
 */
const DEFAULT_INTEGRITY_REVIEWERS = "admin@gmail.com";

export function integrityReviewerEmails() {
    // Same rule as the backend: blank means "not configured" and keeps the designated
    // account; a value that names no account refuses everyone.
    const configured = process.env.REACT_APP_INTEGRITY_REVIEWER_EMAILS;
    const source =
        typeof configured === "string" && configured.trim() !== ""
            ? configured
            : DEFAULT_INTEGRITY_REVIEWERS;

    return source
        .split(",")
        .map((email) => email.trim().toLowerCase())
        .filter(Boolean);
}

/** True only for a signed-in admin whose email is on the list. */
export function isIntegrityReviewer(user) {
    const email = String((user && user.email) || "").trim().toLowerCase();
    return !!email && integrityReviewerEmails().includes(email);
}

/** Drops reviewer-only entries for everyone else. Used by the sidebar and the router. */
export function routesFor(routes, user) {
    const reviewer = isIntegrityReviewer(user);
    return (routes || []).filter((route) => !route.reviewerOnly || reviewer);
}
